import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getHostProperties } from "../../../Components/functions/Functions";
// import { useNavigate } from "react-router-dom";
import {
  SelectionGroup,
  SelectionItem,
} from "../../../Components/Selection/Selection";
import { EmptyNft, LightIcon } from "../../../AssetComponents/Images";
import { RentalItem } from "../../../Components/RealEstateProperty/RentalItem";
import { useLocation, useNavigate } from "react-router-dom";
import { PropertyImageWithFallback } from "../../../Components/Images/PropertyImageWithFallback";
import { Fade } from "react-awesome-reveal";
import Home from "../../../Components/Host/Home";
import Building from "../../../Components/Host/Building";
import NFT from "../../../Components/Host/NFT";
import Rental from "../../../Components/Host/Rental";
import Search from "../../../Components/Host/Search";
import Slash from "../../../Components/Host/Slash";
import { toast } from "react-toastify";

const VIEW_FILTERS = ["all", "listed"];
const defaultStats = {
  listedCount: 0,
  verifiedCount: 0,
  rentalActiveCount: 0,
  pendingCount: 0,
  failedCount: 0,
};

export const HostNFTs = ({ pageSize = 40, scrollThreshold = 200 }) => {
  const dispatch = useDispatch();
  const nfts = useSelector((state) => state.nft.nfts);
  const account = useSelector((state) => state.auth.account);
  const [current, setCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isBackgroundUpdating, setIsBackgroundUpdating] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [propertyIds, setPropertyIds] = useState([]);
  const [propertyCache, setPropertyCache] = useState({});
  const [stats, setStats] = useState(defaultStats);
  const [pagination, setPagination] = useState({
    page: 1,
    hasMore: false,
    totalCount: 0,
  });
  const [isStatsCollapsed, setIsStatsCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);
  const searchDebounceRef = useRef(null);
  const refreshTimeoutRef = useRef(null);

  const handleSearchChange = useCallback((event) => {
    const value = event.target.value;
    setSearchInput(value);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(value.trim());
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const loadProperties = useCallback(
    async ({ page = 1, append = false, background = false } = {}) => {
      if (!account) return;

      if (!append && !background) {
        setIsLoading(true);
      }
      if (append || background) {
        setIsBackgroundUpdating(true);
      }
      if (append) {
        setIsLoadMoreLoading(true);
      }

      try {
        const response = await getHostProperties({
          walletAddress: account,
          view: VIEW_FILTERS[current] || VIEW_FILTERS[0],
          search: searchQuery,
          page,
          limit: pageSize,
          dispatch,
        });

        if (response?.success) {
          const fetchedProperties = (response.properties || []).filter(
            (property) => {
              const tokenId = property.token_id || "";
              const isDraftStatus = property.sync_status === "draft";
              const isDraftId =
                typeof tokenId === "string" && tokenId.startsWith("draft");
              return !isDraftStatus && !isDraftId;
            }
          );

          const ids = fetchedProperties.map((property) => property.token_id);

          setPropertyIds((prev) =>
            append ? Array.from(new Set([...prev, ...ids])) : ids
          );

          setPropertyCache((prev) => {
            const next = append ? { ...prev } : {};
            fetchedProperties.forEach((property) => {
              next[property.token_id] = property;
            });
            return next;
          });

          setStats({
            listedCount: response.stats?.listedCount || 0,
            verifiedCount: response.stats?.verifiedCount || 0,
            rentalActiveCount: response.stats?.rentalActiveCount || 0,
            pendingCount: response.stats?.pendingCount || 0,
            failedCount: response.stats?.failedCount || 0,
          });

          setPagination({
            page,
            hasMore: !!response.hasMore,
            totalCount: response.totalCount || ids.length,
          });
        }
      } catch (error) {
        console.error("Error loading host properties:", error);
        toast.error("Failed to load properties");
      } finally {
        if (!append && !background) {
          setIsLoading(false);
        }
        if (append || background) {
          setIsBackgroundUpdating(false);
        }
        if (append) {
          setIsLoadMoreLoading(false);
        }
      }
    },
    [account, current, searchQuery, dispatch, pageSize]
  );

  // 🚀 OPTIMIZED: Load data when account changes
  useEffect(() => {
    if (!account) {
      setPropertyIds([]);
      setPropertyCache({});
      setStats(defaultStats);
      setSearchInput("");
      setSearchQuery("");
      return;
    }
    loadProperties({ page: 1 });
  }, [account, loadProperties]);

  // Refresh data when navigating back to this page (e.g., after unlisting/editing)
  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathnameRef.current;
    
    // Skip if path hasn't changed
    if (prevPath === currentPath) {
      return;
    }
    
    // Update the ref to track the current path
    prevPathnameRef.current = currentPath;
    
    // Only refresh if we navigated FROM a detail/edit page TO the NFTs list page
    const isNavigatingToNFTs = currentPath === '/dashboard/host/nfts';
    const isFromDetailPage = prevPath && (
      prevPath.startsWith('/dashboard/host/nfts/') && 
      prevPath !== '/dashboard/host/nfts'
    );
    
    if (isNavigatingToNFTs && isFromDetailPage && account) {
      const timeoutId = setTimeout(() => {
        loadProperties({ background: true });
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname, account, loadProperties]);

  // 🚀 OPTIMIZED: Refresh data when page becomes visible (user switches back to tab/window)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && 
          location.pathname === "/dashboard/host/nfts" && 
          account) {
        loadProperties({ background: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [location.pathname, account, loadProperties]);

  useEffect(() => {
    const handleListStatusChanged = () => {
      if (refreshTimeoutRef.current) return;
      refreshTimeoutRef.current = setTimeout(() => {
        refreshTimeoutRef.current = null;
        loadProperties({ background: true });
      }, 250);
    };

    const handlePropertyConfirmed = () => {
      if (refreshTimeoutRef.current) return;
      refreshTimeoutRef.current = setTimeout(() => {
        refreshTimeoutRef.current = null;
        loadProperties({ background: true });
      }, 300);
    };

    window.addEventListener("property:listStatusChanged", handleListStatusChanged);
    window.addEventListener("property:confirmed", handlePropertyConfirmed);
    return () => {
      window.removeEventListener("property:listStatusChanged", handleListStatusChanged);
      window.removeEventListener("property:confirmed", handlePropertyConfirmed);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [loadProperties]);

  const listedCounts = stats.listedCount || 0;
  const verifiedCounts = stats.verifiedCount || 0;
  const rentalActiveCounts = stats.rentalActiveCount || 0;

  const getPropertyById = (tokenId) => nfts[tokenId] || propertyCache[tokenId];

  const handleLoadMore = useCallback(() => {
    if (!pagination.hasMore || isLoadMoreLoading) return;
    loadProperties({ page: pagination.page + 1, append: true });
  }, [pagination, isLoadMoreLoading, loadProperties]);

  // Scroll handler for stats collapse + infinite scroll trigger
  const handleScroll = useCallback(
    (e) => {
      const target = e.target;
      const scrollTop = target.scrollTop;

      if (scrollTop > 20 && !isStatsCollapsed) {
        setIsStatsCollapsed(true);
      } else if (scrollTop <= 20 && isStatsCollapsed) {
        setIsStatsCollapsed(false);
      }

      const clientHeight = target.clientHeight;
      const scrollHeight = target.scrollHeight;
      const threshold =
        typeof scrollThreshold === "number" && scrollThreshold > 0
          ? scrollThreshold
          : 200;
      const distanceFromBottom = scrollHeight - clientHeight - scrollTop;

      if (distanceFromBottom <= threshold) {
        handleLoadMore();
      }
    },
    [isStatsCollapsed, scrollThreshold, handleLoadMore]
  );

  return (
    <div className="w-full overflow-auto max-h-[calc(100vh-80px)] h-full p-[6px] sm:p-[10px]">
      <div className="w-full h-full space-y-[12px] sm:space-y-[20px] rounded-[10px] p-[4px] sm:p-[8px] flex flex-col">
        {/* Header Section - Always Visible */}
        <div className="bg-white border border-[#E3E3E3] rounded-[10px] p-3 shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
          <div className="flex items-center gap-2">
            <div className="p-2 w-8 h-8 flex items-center justify-center rounded-full bg-[#5b1dee]">
              <Home className="w-4 h-4 text-white"/>
            </div>
            <div>
              <div className="font-semibold text-lg">My Real Estate Apartments</div>
              <div className="text-gray-600 text-sm">Your Real Estate Status</div>
            </div>
          </div>
        </div>

        {/* Mobile Layout - Stats Cards, Tabs, and Search - All collapse together */}
        <div className={`lg:hidden space-y-[12px] sm:space-y-[16px] transition-all duration-300 ease-in-out ${
          isStatsCollapsed ? 'transform -translate-y-full opacity-0 max-h-0 overflow-hidden' : 'transform translate-y-0 opacity-100'
        }`}>
          {/* Stats Cards - Mobile Grid */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div className="bg-white flex justify-between items-center p-3 rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <Building className="w-4 h-4"/>
                </div>
                <div className="text-[#959595] text-xs">Total Listed</div>
              </div>
              <div className="text-sm font-semibold">{listedCounts}</div>
            </div>
            
            <div className="bg-white flex justify-between items-center p-3 rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <NFT className="w-4 h-4"/>
                </div>
                <div className="text-[#959595] text-xs">Verified</div>
              </div>
              <div className="text-sm font-semibold">{verifiedCounts}</div>
            </div>
            
            <div className="bg-white flex justify-between items-center p-3 rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <Rental className="w-4 h-4"/>
                </div>
                <div className="text-[#959595] text-xs">Active Rentals</div>
              </div>
              <div className="text-sm font-semibold">{rentalActiveCounts}</div>
            </div>
            
            <div className="bg-white flex justify-between items-center p-3 rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <NFT className="w-4 h-4"/>
                </div>
                <div className="text-[#959595] text-xs">Total Revenue</div>
              </div>
              <div className="text-sm font-semibold">0</div>
            </div>
          </div>

          {/* NFT Tabs - Mobile Optimized */}
          <div className="w-full">
            <SelectionGroup
              defaultItem={current}
              className="border-[2px] border-[#e3e3e3] w-full px-2 py-2 gap-2 flex items-center rounded-[16px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
            >
              <SelectionItem
                SelectedItem={
                  <div className="py-2 px-3 cursor-pointer bg-white rounded-[12px] flex-1 flex justify-center items-center gap-2 shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] transition-all duration-200">
                    <div className="text-black font-semibold text-sm">Verified Apartments</div>
                    <LightIcon className="w-4 h-4"/>
                  </div>
                }
                UnselectedItem={
                  <div
                    onClick={() => setCurrent(0)}
                    className="py-2 px-3 cursor-pointer hover:bg-[#f6f6f6] rounded-[12px] flex-1 flex justify-center items-center transition-all duration-200"
                  >
                    <div className="text-[#959595] text-sm font-medium">Verified Apartments</div>
                  </div>
                }
              />
             
              <SelectionItem
                SelectedItem={
                  <div className="py-2 px-3 cursor-pointer bg-white rounded-[12px] flex-1 flex justify-center items-center gap-2 shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] transition-all duration-200">
                    <div className="text-black font-semibold text-sm">My Listings</div>
                    <LightIcon className="w-4 h-4"/>
                  </div>
                }
                UnselectedItem={
                  <div
                    onClick={() => setCurrent(1)}
                    className="py-2 px-3 cursor-pointer hover:bg-[#f6f6f6] rounded-[12px] flex-1 flex justify-center items-center transition-all duration-200"
                  >
                    <div className="text-[#959595] text-sm font-medium">My Listings</div>
                  </div>
                }
              />
            </SelectionGroup>
          </div>

          {/* Search Bar - Mobile Optimized */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="w-4 h-4 text-gray-400"/>
            </div>
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search properties..."
              className="w-full pl-10 pr-10 py-3 bg-white border border-[#E3E3E3] rounded-[10px] text-sm placeholder-gray-400 focus:outline-none focus:border-[#6366F1] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <Slash className="w-4 h-4 text-gray-400"/>
            </div>
          </div>
        </div>

        {/* NFTs Grid - Mobile Optimized - with scroll event */}
        <div 
          className="lg:hidden grid grid-cols-2 gap-3 sm:gap-4 overflow-auto max-h-[calc(100vh-400px)] px-1"
          onScroll={handleScroll}
        >
          {isLoading ? (
            <div className="col-span-2 flex flex-col items-center justify-center py-12">
              <div className="text-sm text-gray-600">Loading Apartments...</div>
            </div>
          ) : propertyIds.length > 0 ? (
            propertyIds.map((nftId) => {
              const property = getPropertyById(nftId);
              if (!property) return null;
              return (
                <Fade key={`nft-fade-${nftId}`}>
                  <div
                    className="relative group cursor-pointer space-y-2"
                    onClick={() => navigate(`/dashboard/host/nfts/${nftId}`)}
                  >
                    <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                      <PropertyImageWithFallback
                        src={property.metaData?.images?.[0]}
                        alt={`Apartment ${nftId}`}
                        className="w-full h-full"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                        <button className="opacity-0 group-hover:opacity-100 bg-white text-black px-2 py-1 rounded text-xs font-medium transition-all duration-200">
                          View
                        </button>
                      </div>
                    </div>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-xs font-medium text-gray-900 truncate">
                        {property.metaData?.buildingName || `Property ${nftId}`}
                      </div>
                    </div>
                    <span className="text-[11px] text-gray-500">#{nftId}</span>
                  </div>
                </Fade>
              );
            })
          ) : (
            <div className="col-span-2 text-center py-12">
              <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                <EmptyNft className="w-6 h-6 text-gray-400"/>
              </div>
              <div className="text-sm font-medium text-gray-700">
                {searchInput ? "No properties found" : "No Apartments yet"}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {searchInput
                  ? "Try adjusting your search terms"
                  : current === 0
                  ? "No Verified Apartments yet"
                  : current === 1
                  ? "No Listed Apartments yet"
                  : "No Apartments yet"}
              </div>
            </div>
          )}
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:block">
          {/* Stats Cards Grid - Slides up completely */}
          <div className={`w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[12px] sm:gap-[16px] transition-all duration-300 ease-in-out ${
            isStatsCollapsed ? 'transform -translate-y-full opacity-0 max-h-0 overflow-hidden' : 'transform translate-y-0 opacity-100'
          }`}>
            <Fade cascade damping={0.2}>
              <div className="bg-white flex justify-between items-center p-[12px] sm:p-[16px] rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                <div className="flex items-center gap-[8px] sm:gap-[10px]">
                  <div className="p-[4px] sm:p-[6px] rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                    <div className="scale-75 sm:scale-100">
                      <Building/>
                    </div>
                  </div>
                  <div className="text-[#959595] text-xs sm:text-sm lg:text-base">Total Apartments Listed</div>
                </div>
                <div className="text-[16px] sm:text-[20px] font-semibold">{listedCounts}</div>
              </div>
             
              <div className="bg-white flex justify-between items-center p-[12px] sm:p-[16px] rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                <div className="flex items-center gap-[8px] sm:gap-[10px]">
                  <div className="p-[4px] sm:p-[6px] rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                    <div className="scale-75 sm:scale-100">
                      <NFT/>
                    </div>
                  </div>
                  <div className="text-[#959595] text-xs sm:text-sm lg:text-base">Verified Apartments</div>
                </div>
                <div className="text-[16px] sm:text-[20px] font-semibold">{verifiedCounts}</div>
              </div>

              <div className="bg-white flex justify-between items-center p-[12px] sm:p-[16px] rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                <div className="flex items-center gap-[8px] sm:gap-[10px]">
                  <div className="p-[4px] sm:p-[6px] rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                    <div className="scale-75 sm:scale-100">
                      <Rental/>
                    </div>
                  </div>
                  <div className="text-[#959595] text-xs sm:text-sm lg:text-base">Active Rentals</div>
                </div>
                <div className="text-[16px] sm:text-[20px] font-semibold">{rentalActiveCounts}</div>
              </div>

              <div className="bg-white flex justify-between items-center p-[12px] sm:p-[16px] rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                <div className="flex items-center gap-[8px] sm:gap-[10px]">
                  <div className="p-[4px] sm:p-[6px] rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                    <div className="scale-75 sm:scale-100">
                      <NFT/>
                    </div>
                  </div>
                  <div className="text-[#959595] text-xs sm:text-sm lg:text-base">Total Revenue</div>
                </div>
                <div className="text-[16px] sm:text-[20px] font-semibold">0</div>
              </div>
            </Fade>
          </div>

          {/* Controls Section - Also collapses with stats */}
          <div className={`px-[10px] sm:px-[20px] w-full flex flex-col sm:flex-row sm:justify-between sm:items-center gap-[12px] sm:gap-[16px] my-[20px] sm:my-[40px] transition-all duration-300 ease-in-out ${
            isStatsCollapsed ? 'transform -translate-y-full opacity-0 max-h-0 overflow-hidden' : 'transform translate-y-0 opacity-100'
          }`}>
            <div className="w-full sm:w-max">
              <SelectionGroup
                defaultItem={current}
                className="border-[2px] border-[#e3e3e3] w-full sm:w-max px-[4px] py-[3px] sm:py-[4px] gap-[4px] sm:gap-[8px] flex items-center rounded-[14px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
              >
                <SelectionItem
                  SelectedItem={
                    <div className="py-[3px] sm:py-[4px] cursor-pointer bg-white rounded-[10px] w-[80px] sm:w-[120px] flex justify-center items-center gap-[6px] sm:gap-[8px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] transition-all duration-200">
                      <div className="text-black font-semibold text-xs sm:text-base">Verified</div>
                      <div className="flex items-center justify-center"><LightIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5" aria-hidden="true"/></div>
                    </div>
                  }
                  UnselectedItem={
                    <div
                      onClick={() => setCurrent(0)}
                      className="py-[3px] sm:py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-[80px] sm:w-[120px] flex justify-center items-center"
                    >
                      <div className="text-[#959595] text-xs sm:text-base">Verified</div>
                    </div>
                  }
                />
               
                <SelectionItem
                  SelectedItem={
                    <div className="py-[3px] sm:py-[4px] cursor-pointer bg-white rounded-[10px] w-[80px] sm:w-[120px] flex justify-center items-center gap-[6px] sm:gap-[8px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] transition-all duration-200">
                      <div className="text-black font-semibold text-xs sm:text-base">Listings</div>
                      <div className="flex items-center justify-center"><LightIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5" aria-hidden="true"/></div>
                    </div>
                  }
                  UnselectedItem={
                    <div
                      onClick={() => setCurrent(1)}
                      className="py-[3px] sm:py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-[80px] sm:w-[120px] flex justify-center items-center"
                    >
                      <div className="text-[#959595] text-xs sm:text-base">Listings</div>
                    </div>
                  }
                />
              </SelectionGroup>
            </div>

            {/* Search Bar */}
            <div className="flex gap-[8px] sm:gap-[10px] items-center border-[#E3E3E3] border-[1px] rounded-[12px] pl-[12px] sm:pl-[16px] pr-[6px] sm:pr-[8px] py-[8px] sm:py-[9px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] w-full sm:w-auto sm:min-w-[250px]">
              <div className="scale-75 sm:scale-100">
                <Search/>
              </div>
              <input 
                value={searchInput}
                onChange={handleSearchChange}
                placeholder="Search" 
                className="flex-1 outline-none text-sm sm:text-base" 
              />
              <div className="scale-75 sm:scale-100">
                <Slash/>
              </div>
            </div>
          </div>

          {/* NFTs Grid - with scroll event for stats collapse */}
          <div 
            className="grid px-[10px] sm:px-[20px] gap-[12px] sm:gap-[20px] w-full overflow-auto max-h-[calc(100vh-200px)] grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            onScroll={handleScroll}
          >
            {isLoading ? (
              <div className="flex flex-col items-center w-full justify-center h-[calc(100vh-600px)] col-span-full">
                <div className="text-sm sm:text-base">Loading Apartments...</div>
              </div>
            ) : propertyIds.length > 0 ? (
              propertyIds.map((nftId) => {
                const property = getPropertyById(nftId);
                if (!property) return null;
                return (
                  <Fade key={`nft-fade-${nftId}`}>
                    <div className="flex flex-col gap-2">
                      <RentalItem
                        key={`rental-${nftId}`}
                        token_id={nftId}
                        tokenData={property}
                        hideWishlist={true}
                      />
                      <div className="text-[11px] text-gray-500 text-right">
                        #{nftId}
                      </div>
                    </div>
                  </Fade>
                );
              })
            ) : (
              <div className="flex flex-col items-center w-full justify-center h-[calc(100vh-600px)] col-span-full">
                <div className="scale-75 sm:scale-100">
                  <EmptyNft />
                </div>
                <div className="text-sm sm:text-base font-semibold">No Apartments yet</div>
                <div className="text-[#737373] text-xs sm:text-sm text-center">
                  {current === 0
                    ? "No Verified Apartments yet"
                    : current === 1
                    ? "No Listed Apartments yet"
                    : "No Apartments yet"}
                </div>
              </div>
            )}
          </div>
        </div>

        {!isLoading && isLoadMoreLoading && (
          <div className="w-full flex justify-center py-4 text-sm text-gray-500">
            Loading more properties...
          </div>
        )}

        {/* Background Update Indicator */}
        {isBackgroundUpdating && (
          <div className="fixed bottom-4 right-4 text-xs sm:text-sm text-gray-500 bg-white px-2 py-1 rounded shadow">
            Refreshing host properties...
          </div>
        )}
      </div>
    </div>
  );
};
